import { SHStack, SHStackProps } from "@components/design-systems";
import { FeatureItemProps } from "@layouts/review/transfer-select-feature-list/components/feature-item";
import {
  FeatureList,
  FeatureListProps,
} from "@layouts/review/transfer-select-feature-list/components/feature-list";
import { FieldTitle } from "@layouts/review/transfer-select-feature-list/components/field-title";
import { ReviewConfigurationFeatureDTO } from "@models/reviews/entities/steps/feature";
import { isEmpty } from "lodash";
import { useMemo } from "react";

export interface TransferSelectFeatureListProps extends SHStackProps {
  features?: ReviewConfigurationFeatureDTO[];
  handleOnSelectDeselectAll?: (
    subGroupId?: string,
    totalFeature?: number,
    isDeselect?: boolean,
  ) => void;
}

export const TransferSelectFeatureList = ({
  subGroupId,
  features = [],
  handleOnSelectDeselect,
  handleOnSelectDeselectEssential,
  handleOnBothSelect,
  handleOnSelectDeselectAll,
  ...others
}: TransferSelectFeatureListProps & FeatureListProps & FeatureItemProps) => {
  const { selectedFeatures, availableFeatures } = useMemo(() => {
    if (!features) return { selectedFeatures: [], availableFeatures: [] };
    return features.reduce(
      ({ selectedFeatures, availableFeatures }, curr) => {
        curr.isSelected
          ? selectedFeatures.push(curr)
          : availableFeatures.push(curr);
        return { selectedFeatures, availableFeatures };
      },
      {
        selectedFeatures: [] as ReviewConfigurationFeatureDTO[],
        availableFeatures: [] as ReviewConfigurationFeatureDTO[],
      },
    );
  }, [features]);

  return (
    <SHStack spacing={3} {...others}>
      <SHStack
        direction="row"
        justifyContent="space-between"
        alignItems={"flex-start"}
        gap="25px"
      >
        <FieldTitle
          width={"50%"}
          title={"Select features"}
          actionText={"Select all"}
          subTitle={"Choose which features to include in the review"}
          handleOnClick={() =>
            handleOnSelectDeselectAll?.(
              subGroupId,
              availableFeatures?.length,
              false,
            )
          }
          isDisable={isEmpty(availableFeatures)}
        />
        <FieldTitle
          width={"50%"}
          title={"Selected features"}
          actionText={"Deselect all"}
          subTitle={
            isEmpty(selectedFeatures)
              ? "You haven’t selected any features yet. Click on one of the features in the column to the left."
              : " Features to be included in the review. Click to remove them"
          }
          handleOnClick={() =>
            handleOnSelectDeselectAll?.(
              subGroupId,
              selectedFeatures?.length,
              true,
            )
          }
          isDisable={isEmpty(selectedFeatures)}
        />
      </SHStack>
      <SHStack
        direction="row"
        justifyContent="space-between"
        alignItems={"flex-start"}
        gap="25px"
      >
        <FeatureList
          subGroupId={subGroupId}
          features={availableFeatures}
          handleOnSelectDeselect={handleOnSelectDeselect}
          handleOnSelectDeselectEssential={handleOnSelectDeselectEssential}
          handleOnBothSelect={handleOnBothSelect}
        />
        <FeatureList
          isDeselect
          subGroupId={subGroupId}
          features={selectedFeatures}
          handleOnSelectDeselect={handleOnSelectDeselect}
          handleOnSelectDeselectEssential={handleOnSelectDeselectEssential}
          handleOnBothSelect={handleOnBothSelect}
        />
      </SHStack>
    </SHStack>
  );
};
